import { Fade, Button, Box, Drawer, Divider } from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import {
  DETAIL_TAB,
  EDisplayType,
  EViewerMode,
  ProjectDetailContext,
} from "../Interfaces/ProjectDetailInterfaces";
import { HelmDrawerContent } from "./HelmDrawerContent";
import { CalcDrawerContent } from "./CalcDrawerContent";
import { Viewer } from "../../../viewer/viewer";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

type buttonProps = {
  open: boolean;
  show: boolean;
  bottomHeight: string;
  onClick: () => void;
  enableResize: (clientY: number) => void;
};

export const DrawerButton = (props: buttonProps) => {
  const { displayType } = useContext(ProjectDetailContext);
  return (
    <>
      {props.open ? (
        <Fade in={props.show}>
          <Button
            variant="contained"
            color="primary"
            sx={{
              height: "24px",
              width: "24px",
              borderRadius: "0",
              minWidth: "10px",
              zIndex: 1300,
              position: "absolute",
              bottom: `calc(${props.bottomHeight} - 1px - ${
                displayType === EDisplayType.iOS ? "28px" : "0px"
              })`,
              right: 0,
              left: 0,
              margin: "0 auto",
            }}
            onClick={() => {
              props.onClick();
            }}
            onMouseDown={(e) => {
              props.enableResize(e.clientY);
            }}
            onTouchStart={(e) => {
              props.enableResize(e.touches[0].clientY);
            }}
          >
            <KeyboardArrowDownIcon />
          </Button>
        </Fade>
      ) : (
        <Button
          variant="contained"
          color="primary"
          sx={{
            height: "24px",
            width: "24px",
            borderRadius: "0",
            minWidth: "10px",
            zIndex: 1300,
            position: "absolute",
            bottom: 0,
            right: 0,
            left: 0,
            margin: "0 auto",
          }}
          onClick={() => {
            props.onClick();
          }}
        >
          <KeyboardArrowUpIcon />
        </Button>
      )}
    </>
  );
};

type Props = {
  type: EViewerMode;
  open: boolean;
  setOpen: () => void;
};

export const Q3DBottomDrawer = (props: Props) => {
  const [showButton, setShowButton] = useState<boolean>(false);
  const [bottomHeight, setBottomHeight] = useState<string>("150px");
  const [borderHighLight, setBorderHighLight] = useState<boolean>(false);
  const { currentViewerMode, projectDetail, detailTab, displayType } =
    useContext(ProjectDetailContext);

  const { height, enableResize } = useResize({
    defaultHeight: Number(bottomHeight.replace("px", "")),
  });

  useEffect(() => {
    setBottomHeight(`${height}px`);
  }, [height]);

  const DrawerContent = (type: EViewerMode) => {
    switch (type) {
      case EViewerMode.CALCULATION:
        return <CalcDrawerContent />;
      case EViewerMode.HELMERT_TRANSFORM:
        return <HelmDrawerContent />;
      default:
        return <></>;
    }
  };

  return (
    <>
      {(currentViewerMode === EViewerMode.CALCULATION ||
        currentViewerMode === EViewerMode.HELMERT_TRANSFORM) &&
        detailTab !== DETAIL_TAB.PHOTO && (
          <Box
            onMouseEnter={() => {
              setShowButton(true);
            }}
            onMouseLeave={() => {
              setShowButton(false);
            }}
          >
            <DrawerButton
              open={props.open}
              show={displayType === EDisplayType.iOS || showButton}
              bottomHeight={bottomHeight}
              onClick={() => {
                props.setOpen();
              }}
              enableResize={enableResize}
            />
            <Drawer
              variant="persistent"
              anchor="bottom"
              open={props.open}
              PaperProps={{
                sx: {
                  height: bottomHeight,
                  display: "flex",
                  justifyContent: "flex-start",
                },
              }}
            >
              <Divider
                variant="fullWidth"
                sx={{
                  cursor: "n-resize",
                  borderWidth: "2px",
                  borderColor: borderHighLight && "rgba(188, 226, 232, 0.5)",
                }}
                onMouseEnter={(e) => {
                  setBorderHighLight(true);
                }}
                onMouseLeave={(e) => {
                  setBorderHighLight(false);
                }}
                onMouseDown={(e) => {
                  enableResize(e.clientY);
                }}
                onTouchStart={(e) => {
                  enableResize(e.touches[0].clientY);
                  setBorderHighLight(true);
                }}
                onTouchEnd={(e) => {
                  setBorderHighLight(false);
                }}
              />
              {DrawerContent(props.type)}
            </Drawer>
          </Box>
        )}
    </>
  );
};

type UseResizeProps = {
  defaultHeight: number;
};

type UseResizeReturn = {
  height: number;
  enableResize: (startY: number) => void;
};

const useResize = ({ defaultHeight }: UseResizeProps): UseResizeReturn => {
  const minHeight = 120;
  const maxHeight = 300;

  const [isResizing, setIsResizing] = useState(false);
  const [height, setHeight] = useState(defaultHeight);
  const [startY, setStartY] = useState(0);

  const enableResize = useCallback(
    (startY: number) => {
      setIsResizing(true);
      setStartY(startY);
    },
    [setIsResizing]
  );

  const disableResize = useCallback(() => {
    setIsResizing(false);
  }, [setIsResizing]);

  const resize = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (isResizing) {
        const clientY =
          e instanceof MouseEvent ? e.clientY : e.touches[0].clientY;
        const offset = startY - clientY;
        const newHeight = height + offset;
        if (newHeight >= minHeight && newHeight <= maxHeight) {
          setHeight(newHeight);
          setStartY(clientY);
        }
      }
    },
    [startY, height, isResizing, setHeight, setStartY]
  );

  useEffect(() => {
    document.addEventListener("mousemove", resize);
    document.addEventListener("mouseup", disableResize);
    document.addEventListener("touchmove", resize);
    document.addEventListener("touchend", disableResize);

    return () => {
      document.removeEventListener("mousemove", resize);
      document.removeEventListener("mouseup", disableResize);
      document.removeEventListener("touchmove", resize);
      document.removeEventListener("touchend", disableResize);
    };
  }, [disableResize, resize]);

  return { height, enableResize };
};
